import React, { useState } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Button,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import logo from "../../Assets/logo.svg";
import { toast, ToastContainer } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { forgetPassword } from "../../Services/LoginApi";
import Grid from "@mui/material/Grid2";

const PasswordSetup = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setConfirmShowPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState<any>("");
  const [pwdError, setPwdError] = useState("");
  const [newPwdError, setnewPwdError] = useState("");
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email;
  const otpCode = location.state?.otpCode;

  const handleClickShowPassword = () => setShowPassword((prev) => !prev);
  const handleClickShowConfirmPassword = () =>
    setConfirmShowPassword((prev) => !prev);

  const validatePassword = (pwd:any) => {
    const minLength = /.{14,}/;
    const upperCase = /[A-Z]/;
    const lowerCase = /[a-z]/;
    const number = /[0-9]/;
    const specialChar = /[!@#$%^&*(),.?":{}|<>]/;
    const commonPasswords = [
      "password",
      "12345678",
      "qwerty",
      "admin",
      "letmein",
    ];

    if (!minLength.test(pwd)) return "Must be at least 14 characters long.";
    if (!upperCase.test(pwd)) return "Must include at least one uppercase letter.";
    if (!lowerCase.test(pwd)) return "Must include at least one lowercase letter.";
    if (!number.test(pwd)) return "Must include at least one number.";
    if (!specialChar.test(pwd))
      return "Must include at least one special character.";
    if (commonPasswords.includes(pwd.toLowerCase()))
      return "Cannot be a commonly used password.";

    return "";
  };
  const handlePasswordChange = (e: any) => {
    const value = e.target.value;
    setPassword(value);
    setPwdError(validatePassword(value));
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) {
      setPwdError("Please enter a new password!");
    } else {
      setPwdError("");
    }
    if (!confirmPassword.trim()) {
      setnewPwdError("Please confirm your new password!");
    } else {
      setnewPwdError("");
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match!");
      return;
    }

    setLoading(true);
    try {
      const response = await forgetPassword(email, password, otpCode);
      if (response.success) {
        toast.success(response.message || "Password reset successfully!");
        navigate("/login");
      } else {
        toast.error(response.error || "Failed to reset password.");
      }
    } catch (error) {
      toast.error("Error processing your request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, overflowY: "auto", overflowX:'hidden' }}>
      <Grid container spacing={2} className="login">
        <Grid
          size={{ xs: 12, md: 5, sm: 12 }}
          className="login__left"
          sx={{ display: { xs: "none", sm: "none", md: "flex" } }}
        >
          <div className="login__left-image">
            <img src={logo} alt="logo" />
          </div>
          <div className="login__left-title">
            <p>Experience Management Portal</p>
          </div>
        </Grid>

        <Grid size={{ xs: 12, md: 7, sm: 12 }} className="login__right">
          <div className="login__container login_main">
            <Grid
              size={{ xs: 12, md: 5, sm: 12 }}
              className="login__left"
              sx={{ display: { xs: "block", sm: "block", md: "none" } }}
            >
              <div className="login__left-image">
                <img src={logo} alt="logo" />
              </div>
              <div className="login__left-title">
                <p>Experience Management Portal</p>
              </div>
            </Grid>

            <h1>Password Setup</h1>
            <p>Enter New Password</p>

            <form className="login__form " onSubmit={handleResetPassword}>
              <TextField
                label="New Password"
                type={showPassword ? "text" : "password"}
                value={password}
                error={!!pwdError}
                helperText={pwdError}
                onChange={handlePasswordChange}
                variant="standard"
                fullWidth
                className="primary-focused-textfield w-100"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClickShowPassword} edge="end">
                        {!showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
              <TextField
                className="primary-focused-textfield w-100"
                label="Confirm Password"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                error={!!newPwdError}
                helperText={newPwdError}
                variant="standard"
                fullWidth
                onChange={(e) => {
                  const value = e.target.value;
                  setConfirmPassword(value);
                  if (!value.trim()) {
                    setnewPwdError("Please confirm your new password!");
                  } else if (password && value !== password) {
                    setnewPwdError("Passwords do not match!");
                  } else {
                    setnewPwdError("");
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClickShowConfirmPassword}
                        edge="end"
                      >
                        {!showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
              <div className="login__password-list">
                <ul>
                  <li> Must be 14 characters or longer.</li>
                  <li>
                    {" "}
                    Must include uppercase, lowercase, numbers, and special
                    characters.
                  </li>
                  <li> Cannot be a commonly used or compromised password.</li>
                </ul>
              </div>
              <div className="login__button">
                <Button
                  type="submit"
                  disabled={loading}
                  variant="contained"
                  loading={loading}
                >
                  Set Password
                </Button>
              </div>
            </form>
          </div>
          <ToastContainer position="top-right" autoClose={3000} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default PasswordSetup;
